if (!customElements.get('dyld-prize-slider')) {
  customElements.define('dyld-prize-slider', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.dyld-prize__track');
      this.slides = [...this.querySelectorAll('.dyld-prize__slide')];
      this.controls = this.querySelector('.dyld-prize__controls');
      if (!this.track || !this.controls || this.slides.length < 2) return;

      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.toggle = this.querySelector('[name="rotation"]');
      this.fill = this.querySelector('[data-progress-fill]');
      this.count = this.querySelector('[data-photo-count]');
      this.status = this.querySelector('[data-gallery-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.duration = Math.min(10, Math.max(3, Number(this.dataset.interval) || 5)) * 1000;
      this.wantsRotation = this.dataset.autoplay === 'true' && !this.motion.matches && !window.Shopify?.designMode;
      this.inView = false;
      this.hovered = false;
      this.moving = false;

      this.toggle?.addEventListener('click', () => {
        this.wantsRotation = !this.wantsRotation;
        this.syncRotation();
      }, options);
      this.querySelector('[name="previous"]').addEventListener('click', () => this.navigate(-1, true), options);
      this.querySelector('[name="next"]').addEventListener('click', () => this.navigate(1, true), options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.syncRotation();
      }, options);
      this.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.syncRotation();
      }, options);
      this.addEventListener('focusin', event => {
        // Keyboard exploration stays paused until the customer chooses Play.
        if (event.target !== this.toggle) this.stopRotation();
      }, options);
      this.track.addEventListener('pointerdown', () => this.stopRotation(), options);
      this.track.addEventListener('wheel', () => this.stopRotation(), { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        this.stopRotation();
        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.maxScroll, behavior: 'instant' });
        }
      }, options);
      this.track.addEventListener('scroll', () => {
        this.moving = true;
        this.syncRotation();
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          this.moving = false;
          this.updateCount();
          this.resetProgress();
        }, 160);
      }, { ...options, passive: true });
      document.addEventListener('visibilitychange', () => this.syncRotation(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.stopRotation();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        this.stopRotation();
        const slide = event.target.closest('.dyld-prize__slide');
        if (slide) this.track.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
      }, options);

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting && entry.intersectionRatio >= .5;
        this.syncRotation();
      }, { threshold: [0, .5] });
      this.visibilityObserver.observe(this);
      this.measure();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.animation?.cancel();
      clearTimeout(this.settleTimer);
    }

    measure() {
      this.maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.positions = [...new Set(this.slides.map(slide => Math.min(slide.offsetLeft, this.maxScroll)))];
      this.controls.hidden = this.maxScroll < 2;
      this.updateCount();
      this.resetProgress();
    }

    updateCount() {
      const left = this.track.scrollLeft;
      const right = left + this.track.clientWidth;
      const visible = this.slides.map((slide, index) => ({ slide, index })).filter(({ slide }) =>
        slide.offsetLeft >= left - 2 && slide.offsetLeft + slide.clientWidth <= right + 2
      );
      const first = (visible[0]?.index ?? 0) + 1;
      const last = (visible.at(-1)?.index ?? first - 1) + 1;
      this.count.textContent = `${String(first).padStart(2, '0')}${last > first ? `–${String(last).padStart(2, '0')}` : ''} / ${String(this.slides.length).padStart(2, '0')}`;
      const singular = this.dataset.itemName || 'Photo';
      const plural = this.dataset.itemsName || 'Photos';
      this.status.textContent = last > first ? `${plural} ${first} to ${last} of ${this.slides.length}` : `${singular} ${first} of ${this.slides.length}`;
    }

    stopRotation() {
      this.wantsRotation = false;
      this.syncRotation();
    }

    navigate(direction, manual = false) {
      if (manual) this.stopRotation();
      const left = this.track.scrollLeft;
      const target = direction > 0
        ? this.positions.find(position => position > left + 2) ?? 0
        : this.positions.findLast(position => position < left - 2) ?? this.maxScroll;
      this.track.scrollTo({ left: target, behavior: this.motion.matches || Math.abs(target - left) > this.track.clientWidth ? 'instant' : 'smooth' });
    }

    resetProgress() {
      this.animation?.cancel();
      if (!this.fill) { this.syncRotation(); return; }
      this.animation = this.fill.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
        duration: this.duration, easing: 'linear', fill: 'forwards'
      });
      this.animation.pause();
      this.animation.onfinish = () => {
        if (this.canRotate()) this.navigate(1);
      };
      this.syncRotation();
    }

    canRotate() {
      return this.wantsRotation && this.inView && !this.hovered && !document.hidden && !this.moving && !this.controls.hidden;
    }

    syncRotation() {
      if (this.toggle) {
        this.toggle.dataset.playing = String(this.wantsRotation);
        this.toggle.setAttribute('aria-label', this.wantsRotation ? 'Pause slideshow' : 'Play slideshow');
      }
      this.status.setAttribute('aria-live', this.wantsRotation ? 'off' : 'polite');
      if (!this.animation) return;
      if (this.canRotate()) this.animation.play();
      else this.animation.pause();
    }
  });
}
